import React, { useEffect, useId, useMemo, useState } from 'react';
import * as Alert from '@radix-ui/react-alert-dialog';
import { sanitizeEmailInput } from '../../utils/inputSanitization';

// ResetPasswordDialog centralizes the UX for requesting a password reset email.
// It owns field validation, loading state and success/err display, while delegating
// the actual send logic to the parent via onSubmit(email).
export default function ResetPasswordDialog({
  open,
  onOpenChange,
  onSubmit,          // async (email) => void (throws on error)
  initialEmail = '',
  // The RMS portal passes its own designed wording; the defaults stay as the
  // Assessment platform's, which shares this dialog and has not been redesigned.
  title = 'Forgotten Password',
  description = "Enter your email address and we'll send you instructions to reset your password.",
  // Opt-in, so the Assessment platform, which shares this dialog, renders
  // exactly as it did: no label, its original placeholder, its own wording.
  emailLabel = null,
  emailHint = '',
  submitLabel = 'Send Password Reset Email',
  successBody = null
}) {
  const [email, setEmail] = useState(initialEmail || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fieldId = useId();
  const emailFieldId = `${fieldId}-email`;
  const emailHintId = `${fieldId}-email-hint`;

  useEffect(() => {
    if (open) {
      setEmail(initialEmail || '');
      setError('');
      setSuccess(false);
      setIsLoading(false);
    }
  }, [open, initialEmail]);

  const isValidEmail = useMemo(() => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);

  const handleClose = () => {
    onOpenChange && onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!isValidEmail) {
      setError('Please enter a valid email address');
      return;
    }
    try {
      setIsLoading(true);
      await onSubmit(email);
      setSuccess(true);
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Alert.Root open={open} onOpenChange={onOpenChange}>
      <Alert.Portal>
        <Alert.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <Alert.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-[500px] shadow-2xl drop-shadow-lg">
          <Alert.Title className="text-xl font-bold mb-4">{title}</Alert.Title>

          {!success ? (
            <>
              <Alert.Description className="mb-4">
                {description}
              </Alert.Description>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  {/* A visible label and hint where the caller supplies them
                      (GOV.UK question pattern); otherwise the field is left
                      exactly as it was. */}
                  {emailLabel && (
                    <label htmlFor={emailFieldId} className="block font-semibold mb-1">
                      {emailLabel}
                    </label>
                  )}
                  {emailHint && (
                    <p id={emailHintId} className="text-sm text-gray-600 mb-2">{emailHint}</p>
                  )}
                  <input
                    id={emailFieldId}
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(sanitizeEmailInput(e.target.value)); setError(''); }}
                    placeholder={emailLabel ? undefined : 'Enter your email'}
                    aria-describedby={emailHint ? emailHintId : undefined}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring focus:ring-primary/90"
                    disabled={isLoading}
                  />
                  <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-12 mt-1' : 'max-h-0 mt-0'}`}>
                    <p className={`text-red-500 text-xs ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>{error}</p>
                  </div>
                </div>
                <div className="flex justify-end gap-4">
                  <Alert.Cancel asChild>
                    <button type="button" className="font-semibold px-4 py-2 text-gray-500 hover:text-gray-700" disabled={isLoading} onClick={handleClose}>
                      Cancel
                    </button>
                  </Alert.Cancel>
                  <button type="submit" className="font-semibold px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-primary/30" disabled={isLoading || !isValidEmail}>
                    {isLoading ? 'Sending...' : submitLabel}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              {/* Visible success panel */}
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-semibold text-green-900">Check your email</span>
                  </div>
                  <p className="text-green-800">
                    {successBody
                      ? successBody(email)
                      : `If an account exists for ${email}, you will receive password reset instructions.`}
                  </p>
                  {successBody && (
                    <p className="text-green-800 mt-2">Nothing arrived? Check your junk folder.</p>
                  )}
                </div>
              </div>
              {/* a11y: provide semantic description for SR users without adding visible text */}
              <Alert.Description className="sr-only">
                Password reset email sent. If an account exists for the entered address, a link has been sent.
              </Alert.Description>
              <div className="flex justify-end">
                <Alert.Cancel asChild>
                  <button className="font-semibold px-4 py-2 text-gray-500 hover:text-gray-700" onClick={handleClose}>Close</button>
                </Alert.Cancel>
              </div>
            </>
          )}
        </Alert.Content>
      </Alert.Portal>
    </Alert.Root>
  );
}


